import React, { useState, useEffect, useRef } from "react";
import ReactModal from "react-modal";

import styles from "./liveticker.module.scss";

import EventsPanel from "../../components/LiveTicker/EventsPanel/EventsPanel";
import TimerBlock from "../../components/LiveTicker/TimerBlock/TimerBlock";
import SidePanel from "../../components/LiveTicker/SidePanel";
import { formatMatches } from "../../utils/format-matches";
import { periodTimer } from "../../utils/period-timer";
import {
    EVENT_TYPES,
    SHOOTOUT_EVENT,
    GOAL_TYPES,
    PS_TYPES,
    PC_CORNERGOAL_TYPES,
    REASONS_FOR_CARDS,
    initQuarters,
} from "../../components/LiveTicker/params";

const getContacts = async (contacts) => {
    if (!contacts.length) {
        return;
    }
    const locale = "de";
    let baseUrl = location.origin;
    const endpoint =
        baseUrl +
        `/admin/api/contacts?locale=${locale}&ids=${contacts}&flat=true`;
    try {
        const playerRequest = await fetch(endpoint);
        const data = await playerRequest.json();
        if (data) {
            const contactsArr = data["_embedded"].contacts;
            return contactsArr;
        }
    } catch (e) {
        console.error(e.message);
    }
};

const formatData = (data, homePlayers, awayPlayers, homeLogo, awayLogo) => {
    const { homeTeam, awayTeam } = data;
    const home = {
        id: homeTeam,
        side: "home",
        teamColor: "#86B99D",
        teamName: data.homeTeamName,
        players: homePlayers,
        logo: homeLogo,
    };

    const away = {
        id: awayTeam,
        side: "away",
        teamColor: "#BDD3F1",
        teamName: data.awayTeamName,
        players: awayPlayers,
        logo: awayLogo,
    };
    return {
        away,
        home,
    };
};
const formatContactData = (
    allContactsInfo,
    contactsIds,
    playersData,
    playersFlag = false,
) => {
    let formattedContacts = contactsIds.map((contactId) => {
        const contactInfo = allContactsInfo.find(
            (contact) => contact.id === contactId,
        );
        const { firstName, lastName, id } = contactInfo;
        let playerFullName = `${firstName} ${lastName}`;
        let baseData = {
            firstName,
            lastName,
            id,
            playerName: playerFullName,
        };

        if (playersFlag) {
            const player = Array.isArray(playersData)
                ? playersData.find((p) => p.contact === id)
                : playersData?.[id];

            return {
                ...baseData,
                playerNumber: player.number,
                isCaptain: player.isCaptain,
                isGoalkeeper: player.isGoalkeeper,
                isSubstituteGoalkeeper: player.isSubstituteGoalkeeper,
                cards: [
                    player.hasGreenCard && "GRÜN",
                    player.hasYellowCard && "GELB",
                    player.hasOrangeCard && "GELB ROT",
                    player.hasRedCard && "ROT",
                ].filter(Boolean),
            };
        }

        return baseData;
    });

    if (playersFlag) {
        formattedContacts.sort((a, b) => a.playerNumber - b.playerNumber);
    }

    return formattedContacts;
};

const getLogos = async (logoIds) => {
    if (!logoIds.length) {
        return;
    }
    const locale = "de";
    let baseUrl = location.origin;
    const endpoint =
        baseUrl + `/admin/api/media?locale=${locale}&ids=${logoIds}`;
    try {
        const request = await fetch(endpoint);
        const response = await request.json();
        return response["_embedded"].media;
    } catch (e) {
        console.error(e.message);
    }
};

const formatApiData = (data, sides, setEventsToShow, ...players) => {
    const { matchPosts, status, homeGoals, awayGoals, formatObject } = data;

    const format = formatObject;
    const periodAbbreviation = format.periodAbbreviation;
    const periodFullName = format.periodName;
    const [quarter, minutes] = formatMatches(format);
    const gameInTotal = quarter * (minutes * 60);

    const formattedPosts = matchPosts.map((post) => {
        let periodNumber = post.quarter;
        let periodAbbr = periodAbbreviation + periodNumber;
        let event = "";
        let quarterSelected = "";
        let typeOfEventTypeSelected = "";
        let typeOfCornerGoalSelected = "";
        let typeOfPsGoalSelected = "";
        let typeOfGoal = "";
        let numberOfPeriods = quarter;
        const goalEvents = [
            "Goals",
            "Penalty Shootout",
            "7m",
            "Penalty Corner",
        ];
        const quarterEventsIds = [1, 2, 3, 4, 5, 6, 7, 8];
        if (goalEvents.includes(post.type)) {
            event = EVENT_TYPES.find((ev) => ev.name === "GOAL");
            const pcEvent = EVENT_TYPES.find((ev) => ev.name === "PC");
            switch (post.postType) {
                case 9:
                    typeOfEventTypeSelected = { id: 0, name: "Feld" };
                    break;
                case 18:
                    typeOfEventTypeSelected = { id: 1, name: "Ecke" };
                    typeOfCornerGoalSelected = true;
                    break;
                case 28:
                    typeOfEventTypeSelected = { name: "Miss" };
                    typeOfPsGoalSelected = { name: "Miss" };
                    typeOfCornerGoalSelected = true;
                    break;
                case 19:
                    typeOfEventTypeSelected = { id: 2, name: "7 Meter" };
                    typeOfCornerGoalSelected = true;
                    break;
                case 16:
                    typeOfPsGoalSelected = { name: "Miss" };
                    typeOfGoal = { name: "Miss" };
                    typeOfEventTypeSelected = { id: 2, name: "7 Meter" };
                    break;
                case 26:
                    event = pcEvent;
                    typeOfEventTypeSelected = { name: "Daneben" };
                    typeOfCornerGoalSelected = true;
                    typeOfPsGoalSelected = true;
                    break;
                case 33:
                    event = pcEvent;
                    typeOfEventTypeSelected = { name: "Treffer" };
                    typeOfCornerGoalSelected = { name: "Direkter Schuss" };
                    break;
                case 34:
                    event = pcEvent;
                    typeOfCornerGoalSelected = { name: "Stecher" };
                    typeOfEventTypeSelected = { name: "Treffer" };
                    break;
                case 35:
                    event = pcEvent;
                    typeOfCornerGoalSelected = { name: "Variante" };
                    typeOfEventTypeSelected = { name: "Treffer" };
                    break;
                case 36:
                    event = pcEvent;
                    typeOfCornerGoalSelected = { name: "Rebound" };
                    typeOfEventTypeSelected = { name: "Treffer" };
                    break;
            }
        }
        if (post.type === "Cards") {
            const { postType } = post;

            switch (postType) {
                case 12:
                    event = EVENT_TYPES.find((ev) => ev.name === "ROT");
                    break;
                case 11:
                    event = EVENT_TYPES.find((ev) => ev.name === "GRÜN");
                    break;
                case 10:
                    event = EVENT_TYPES.find((ev) => ev.name === "GELB");
                    break;
                case 37:
                    event = EVENT_TYPES.find((ev) => ev.name === "GELB ROT");
                    break;
            }
        }

        if (post.postType === 14) {
            event = EVENT_TYPES.find((ev) => ev.name === "PS");
        }

        if (post.postType === 17 || quarterEventsIds.includes(post.postType)) {
            event = EVENT_TYPES.find((ev) => ev.name === "COMMENT");
        }

        switch (post.quarter) {
            case 1:
                quarterSelected = "Q1";
                break;
            case 2:
                quarterSelected = "Q2";
                break;
            case 3:
                quarterSelected = "Q3";
                break;
            case 4:
                quarterSelected = "Q4";
                break;
            case 0:
                quarterSelected = "SO";
                break;
            default:
                quarterSelected = "Q1";
        }
        let playerSelected = "";
        const postPlayerId =
            post.type === "Penalty Shootout"
                ? post.shooterContact
                : post.player;
        if (post.isStaff) {
            Object.keys(data.allMatchStaff).forEach((key) => {
                if (key == postPlayerId) {
                    const value = data.allMatchStaff[key];
                    playerSelected = value;
                }
            });
        } else {
            playerSelected = players.find(
                (player) => player.id === postPlayerId,
            );
        }
        const sideSelected =
            post.team === sides.home.id ? sides.home : sides.away;
        const sideNotSelected =
            sideSelected.id === sides.home.id ? sides.away : sides.home;
        const shootoutGoalkeepers = sideNotSelected?.players?.filter(
            (player) => player?.isGoalkeeper || player?.isSubstituteGoalkeeper,
        );
        let dataToShow = {
            id: post.id,
            sideSelected,
            sideNotSelected,
            playerSelected,
            quarterSelected,
            timestamp: post.minute * 60,
            periodName: periodFullName,
            numberOfPeriods: numberOfPeriods,
            periodAbbreviation: periodAbbreviation,
            loaded: true,
            [sideSelected.side]: {
                ...sideSelected,
                goals:
                    sideSelected.side === "home"
                        ? post.homeGoals
                        : post.awayGoals,
            },
            [sideNotSelected.side]: {
                ...sideNotSelected,
                goals:
                    sideNotSelected.side === "away"
                        ? post.awayGoals
                        : post.homeGoals,
            },
            shootoutGoalkeepers,
            liveEvent: post.isLive === null ? true : post.isLive,
            type: event,
            ...(post.postType === 32 ? { penaltyShoot: true } : {}),
            ...(post.postType === 24 ? { penaltyShootGoal: true } : {}),
            ...(post.postType === 25 ? { penaltyShootMiss: true } : {}),
            eventNotes: post.additionalData ? post.additionalData : "",
            suspensionTime: {
                minutes: post.suspensionMinutes,
            },
        };

        if (post.type == "Penalty Shootout") {
            dataToShow.timestamp = gameInTotal;
        }

        if (
            post.type === "Goals" ||
            post.type === "7m" ||
            post.type === "Penalty Corner"
        ) {
            dataToShow = {
                ...dataToShow,
                typeOfEventTypeSelected,
                typeOfCornerGoalSelected,
                typeOfPsGoalSelected,
            };
        }

        if (
            (post.postType === 17 ||
                quarterEventsIds.includes(post.postType)) &&
            post.additionalData !== "Start of a shootout"
        ) {
            dataToShow = {
                type: event,
                id: post.id,
                eventNotes: post.additionalData,
                timestamp: post.minute * 60,
                quarterSelected,
                liveEvent: post.isLive === null ? true : post.isLive,
                loaded: true,
                postType: post.postType,
                quarter: periodAbbr,
                periodName: periodFullName,
            };
        }

        if (post.additionalData === "Start of a shootout") {
            dataToShow = {
                loaded: true,
                // Opens the modal to delete it, until the match has ended.
                type: status === 4 ? event : SHOOTOUT_EVENT,
                isShootout: true, //for now
                comment: post.additionalData,
                homeGoals: post.homeGoals,
                awayGoals: post.awayGoals,
                timestamp: post.minute * 60,
                quarter: "SO",
                quarterSelected: "SO",
                id: post.id,
            };
        }
        return dataToShow;
    });

    if (status === 4) {
        let lastPeriod = periodAbbreviation + quarter;
        const completeMatchEvent = {
            loaded: true,
            isCompleted: true,
            quarter: lastPeriod,
            quarterSelected: "SO",
            homeGoals,
            awayGoals,
            comment: "Spiel beendet",
            timestamp: gameInTotal,
            periodName: periodFullName,
            numberOfPeriods: quarter,
            periodAbbreviation,
        };
        formattedPosts.push(completeMatchEvent);
    }
    setEventsToShow(formattedPosts);
};

const LiveTicker = () => {
    const [quarterSelected, setQuarterSelected] = useState(null);
    const [timerSecondsSelected, setTimerSecondsSelected] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEndConfirmationModalOpen, setEndConfirmationModalOpen] =
        useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [eventsToShow, setEventsToShow] = useState([]);
    const [goals, setGoals] = useState({
        home: 0,
        away: 0,
    });
    const [sides, setSides] = useState({});
    const [timersPerQuarter, setTimersPerQuarter] = useState(initQuarters);
    const [eventDetailsToEdit, setEventDetailsToEdit] = useState(null);
    const [eventDetailsToAdd, setEventDetailsToAdd] = useState(null);
    const [componentToInsertInModal, setComponentToInsertInModal] =
        useState("div");
    const [isLiveTickerStaff, setIsLiveTickerStaff] = useState(false);
    const [homePlayers, setHomePlayers] = useState([]);
    const [awayPlayers, setAwayPlayers] = useState([]);
    const [homeStaff, setHomeStaff] = useState([]);
    const [awayStaff, setAwayStaff] = useState([]);
    const [matchStatus, setMatchStatus] = useState(1);
    const [matchId, setMatchId] = useState(null);
    const [matchData, setMatchData] = useState([]);
    const dhbMatchTimers = useRef(null);

    const updateStaffHandler = (
        staff,
        sideSelected,
        card,
        initialState,
        remove,
        allStaff,
    ) => {
        const setStaff = sideSelected === "home" ? setHomeStaff : setAwayStaff;
        setStaff((prevStaff) => {
            const updatedStaff = [...prevStaff];
            let prevIndex = "";
            if (initialState) {
                const prevID = initialState.current.playerSelected.contact;
                prevIndex = updatedStaff.findIndex(
                    (prev) => prev.id === prevID,
                );
            }
            const staffIndex = updatedStaff.findIndex(
                (prev) => prev.id === staff.id,
            );
            if (staffIndex !== -1 && !remove) {
                updatedStaff[staffIndex] = {
                    ...updatedStaff[staffIndex],
                    cards: [card],
                };
            }
            if (prevIndex !== -1 && initialState) {
                if (allStaff) {
                    const selectedStaff = allStaff[staff.contact];
                    const cardName = selectedStaff.hasGreenCard
                        ? "GRÜN"
                        : selectedStaff.hasYellowCard
                          ? "GELB"
                          : selectedStaff.hasOrangeCard
                            ? "GELB ROT"
                            : selectedStaff.hasRedCard
                              ? "ROT"
                              : null;
                    updatedStaff[prevIndex] = {
                        ...updatedStaff[prevIndex],
                        cards: [cardName],
                    };
                } else {
                    updatedStaff[prevIndex] = {
                        ...updatedStaff[prevIndex],
                        cards: [],
                    };
                }
            }
            return updatedStaff;
        });
    };

    useEffect(async () => {
        let href = window.location.href;
        let needle =
            href.indexOf("dhbmatch/") > 0 ? "dhbmatch/" : "dhbmatches/";
        let index = href.indexOf(needle);
        let temp = href.slice(index + needle.length + 3);
        let index2 = temp.indexOf("/");
        let id = "";

        //check for case when there is no '/details' in URL, then there is no need for temp.substr(0, index2)
        if (index2 < 0) {
            id = temp;
        } else {
            id = temp.slice(0, index2);
        }
        setMatchId(id);
        const locale = "de";
        let baseUrl = location.protocol + "//" + location.host;
        let endPointData =
            baseUrl + `/admin/api/dhbmatches/${id}?locale=${locale}`;
        try {
            const requestForMatch = await fetch(endPointData);
            const data = await requestForMatch.json();
            dhbMatchTimers.current = data["dhbMatchTimers"];
            setMatchData(data);
            const { homeGoals, awayGoals } = data;
            const { status } = data;
            if (Number.isInteger(status)) {
                setMatchStatus(status);
            }
            setGoals({
                home: homeGoals,
                away: awayGoals,
            });

            const {
                playersHome,
                playersAway,
                staffHome,
                staffAway,
                allMatchPlayers,
                allMatchStaff,
            } = data;

            const matchFormat = data.formatObject;
            const [, minutes] = formatMatches(matchFormat);

            const quarterLength = minutes * 60;

            Object.keys(initQuarters).forEach((quarter) => {
                initQuarters[quarter].timeLeft = quarterLength;
            });

            const contactsIds = [
                ...playersHome,
                ...playersAway,
                ...staffHome,
                ...staffAway,
            ];
            const contactsInfo = await getContacts(contactsIds);
            const homePlayersData = formatContactData(
                contactsInfo,
                playersHome,
                allMatchPlayers,
                true,
            );

            const awayPlayersData = formatContactData(
                contactsInfo,
                playersAway,
                allMatchPlayers,
                true,
            );
            const homeStaffData = formatContactData(
                contactsInfo,
                staffHome,
                allMatchStaff,
                true,
            );
            const awayStaffData = formatContactData(
                contactsInfo,
                staffAway,
                allMatchStaff,
                true,
            );
            const logos = await getLogos([
                data.homeTeamObject?.logo,
                data.awayTeamObject?.logo,
            ]);
            const homeLogo = logos.find(
                (logo) => logo.id === data.homeTeamObject?.logo,
            )?.thumbnails?.["image-150.webp"];
            const awayLogo = logos.find(
                (logo) => logo.id === data.awayTeamObject?.logo,
            )?.thumbnails?.["image-150.webp"];
            const newSides = formatData(
                data,
                homePlayersData,
                awayPlayersData,
                homeLogo,
                awayLogo,
            );
            setHomePlayers(homePlayersData);
            setAwayPlayers(awayPlayersData);
            setHomeStaff(homeStaffData);
            setAwayStaff(awayStaffData);
            setSides(newSides);
            formatApiData(
                data,
                newSides,
                setEventsToShow,
                ...homePlayersData,
                ...awayPlayersData,
            );
        } catch (error) {
            console.log(error);
        }
    }, []);

    useEffect(() => {
        if (
            typeof matchData === "object" &&
            !Array.isArray(matchData) &&
            matchData !== null
        ) {
            const {
                quarter1,
                quarter2,
                quarter3,
                quarter4,
                matchHasShootout,
                matchPosts,
            } = matchData;
            const isShootout = matchPosts?.some(
                (post) => post.additionalData === "Start of a shootout",
            );
            const matchFormat = matchData.formatObject;
            const [, minutes] = formatMatches(matchFormat);

            const quarterLength = minutes * 60;
            switch (true) {
                case quarter4:
                    setTimersPerQuarter((data) => {
                        return {
                            ...data,
                            Q1: {
                                ...data["Q1"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                            Q2: {
                                ...data["Q2"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                            Q3: {
                                ...data["Q3"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                            Q4: {
                                ...data["Q4"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                                timeLeft: 0,
                            },
                        };
                    });
                    if (matchHasShootout && isShootout) {
                        setQuarterSelected("SO");
                        break;
                    } else {
                        setQuarterSelected("Q4");
                        break;
                    }
                case quarter3:
                    setTimersPerQuarter((data) => {
                        return {
                            ...data,
                            Q1: {
                                ...data["Q1"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                            Q2: {
                                ...data["Q2"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                            Q3: {
                                ...data["Q3"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                        };
                    });
                    setQuarterSelected("Q4");
                    break;
                case quarter2:
                    setTimersPerQuarter((data) => {
                        return {
                            ...data,
                            Q1: {
                                ...data["Q1"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                            Q2: {
                                ...data["Q2"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                        };
                    });
                    setQuarterSelected("Q3");
                    break;
                case quarter1:
                    setTimersPerQuarter((data) => {
                        return {
                            ...data,
                            Q1: {
                                ...data["Q1"],
                                quarterEnded: true,
                                timePlayed: quarterLength,
                            },
                        };
                    });
                    setQuarterSelected("Q2");
                    break;
                default:
                    setQuarterSelected("Q1");
            }
        }
    }, [matchData]);

    const isFirstRun = useRef(true);
    const isFirstChange = useRef(true);
    useEffect(() => {
        if (!isFirstRun.current && isFirstChange.current) {
            if (!dhbMatchTimers.current) {
                return;
            }
            if (`Q${dhbMatchTimers.current.quarter}` !== quarterSelected) {
                return;
            }
            isFirstChange.current = false;
            const quarter = timersPerQuarter[quarterSelected];
            const dhbTimer = dhbMatchTimers.current;

            const matchFormat = matchData.formatObject;
            const [, minutes] = formatMatches(matchFormat);
            const quarterLength = minutes * 60;

            setTimersPerQuarter((details) => {
                if (quarter.id === "Q4") {
                    details.Q1 = {
                        ...details.Q1,
                        timePlayed: quarterLength,
                        timeLeft: 0,
                    };
                    details.Q2 = {
                        ...details.Q2,
                        timePlayed: quarterLength,
                        timeLeft: 0,
                    };
                    details.Q3 = {
                        ...details.Q3,
                        timePlayed: quarterLength,
                        timeLeft: 0,
                    };
                }
                if (quarter.id === "Q3") {
                    details.Q1 = {
                        ...details.Q1,
                        timePlayed: quarterLength,
                        timeLeft: 0,
                    };
                    details.Q2 = {
                        ...details.Q2,
                        timePlayed: quarterLength,
                        timeLeft: 0,
                    };
                }
                if (quarter.id === "Q2") {
                    details.Q1 = {
                        ...details.Q1,
                        timePlayed: quarterLength,
                        timeLeft: 0,
                    };
                }
                return {
                    ...details,
                    [quarter?.id]: {
                        ...details[quarter?.id],
                        ...periodTimer(dhbTimer, quarterLength),
                    },
                };
            });
        }
        isFirstRun.current = false;
    }, [quarterSelected]);

    return (
        <>
            <div className={styles["liveticker"]} id="live-ticker">
                <div className={styles["liveticker__container"]}>
                    <SidePanel
                        players={homePlayers}
                        staff={homeStaff}
                        side={sides.home}
                        events={eventsToShow.filter(
                            (event) => event.sideSelected?.id === sides.home.id,
                        )}
                        status={matchStatus}
                    />

                    <EventsPanel
                        eventsToShow={eventsToShow}
                        setIsEditModalOpen={setIsEditModalOpen}
                        setEventDetailsToEdit={setEventDetailsToEdit}
                        setEventsToShow={setEventsToShow}
                        setIsEditing={setIsEditing}
                        setComponentToInsertInModal={
                            setComponentToInsertInModal
                        }
                        matchData={matchData}
                        matchId={matchId}
                        setMatchData={setMatchData}
                    />

                    <SidePanel
                        players={awayPlayers}
                        staff={awayStaff}
                        side={sides.away}
                        events={eventsToShow.filter(
                            (event) => event.sideSelected?.id === sides.away.id,
                        )}
                        status={matchStatus}
                    />
                </div>

                <TimerBlock
                    EVENT_TYPES={EVENT_TYPES}
                    setIsModalOpen={setIsModalOpen}
                    sides={sides}
                    setQuarterSelected={setQuarterSelected}
                    timersPerQuarter={timersPerQuarter}
                    quarterSelected={quarterSelected ? quarterSelected : "Q1"}
                    timerSecondsSelected={timerSecondsSelected}
                    setTimerSecondsSelected={setTimerSecondsSelected}
                    setTimersPerQuarter={setTimersPerQuarter}
                    setEventDetailsToAdd={setEventDetailsToAdd}
                    setSides={setSides}
                    goals={goals}
                    setGoals={setGoals}
                    setIsEditing={setIsEditing}
                    setComponentToInsertInModal={setComponentToInsertInModal}
                    isLiveTickerStaff={isLiveTickerStaff}
                    matchId={matchId}
                    setMatchStatus={setMatchStatus}
                    matchStatus={matchStatus}
                    matchData={matchData}
                    setMatchData={setMatchData}
                    setEndConfirmationModalOpen={setEndConfirmationModalOpen}
                    setEventsToShow={setEventsToShow}
                    formatApiData={formatApiData}
                    homePlayers={homePlayers}
                    awayPlayers={awayPlayers}
                />
            </div>
            <ReactModal
                isOpen={isModalOpen}
                contentLabel="Event Modal"
                ariaHideApp={false}
                style={{
                    content: {
                        backgroundColor: "#E5E5E5",
                        width: "50%",
                        height: "70%",
                        padding: 0,
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                    },
                }}
            >
                {React.createElement(componentToInsertInModal, {
                    setIsModalOpen: setIsModalOpen,
                    EVENT_TYPES: EVENT_TYPES,
                    sides: sides,
                    setEventsToShow: setEventsToShow,
                    GOAL_TYPES: GOAL_TYPES,
                    eventDetailsToAdd: eventDetailsToAdd,
                    setEventDetailsToAdd: setEventDetailsToAdd,
                    goals: goals,
                    setGoals: setGoals,
                    isEditing: isEditing,
                    setIsEditing: setIsEditing,
                    PS_TYPES: PS_TYPES,
                    PC_CORNERGOAL_TYPES: PC_CORNERGOAL_TYPES,
                    REASONS_FOR_CARDS: REASONS_FOR_CARDS,
                    setSides: setSides,
                    eventsToShow: eventsToShow,
                    matchId: matchId,
                    matchData: matchData,
                    setMatchData: setMatchData,
                    formatApiData: formatApiData,
                    homePlayers: homePlayers,
                    awayPlayers: awayPlayers,
                    homeStaff: homeStaff,
                    awayStaff: awayStaff,
                    updateStaffHandler: updateStaffHandler,
                })}
            </ReactModal>

            <ReactModal
                isOpen={isEditModalOpen}
                contentLabel="Edit Event Modal"
                ariaHideApp={false}
                style={{
                    content: {
                        backgroundColor: "#E5E5E5",
                        width: "50%",
                        height: "80%",
                        padding: 0,
                    },
                }}
            >
                {React.createElement(componentToInsertInModal, {
                    setIsEditModalOpen: setIsEditModalOpen,
                    EVENT_TYPES: EVENT_TYPES,
                    sides: sides,
                    setEventsToShow: setEventsToShow,
                    GOAL_TYPES: GOAL_TYPES,
                    eventDetailsToEdit: eventDetailsToEdit,
                    setEventDetailsToEdit: setEventDetailsToEdit,
                    setSides: setSides,
                    goals: goals,
                    setGoals: setGoals,
                    isEditing: isEditing,
                    setIsEditing: setIsEditing,
                    PS_TYPES: PS_TYPES,
                    PC_CORNERGOAL_TYPES: PC_CORNERGOAL_TYPES,
                    REASONS_FOR_CARDS: REASONS_FOR_CARDS,
                    matchData: matchData,
                    matchId: matchId,
                    setMatchData: setMatchData,
                    quarterSelected,
                    homePlayers: homePlayers,
                    awayPlayers: awayPlayers,
                    homeStaff: homeStaff,
                    awayStaff: awayStaff,
                    updateStaffHandler: updateStaffHandler,
                    formatApiData: formatApiData,
                    setTimersPerQuarter: setTimersPerQuarter,
                })}
            </ReactModal>
            <ReactModal
                isOpen={isEndConfirmationModalOpen}
                contentLabel="End Match Modal"
                ariaHideApp={false}
                style={{
                    content: {
                        backgroundColor: "#E5E5E5",
                        padding: 0,
                        width: "50%",
                        height: "max-content",
                        maxWidth: "620px",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                    },
                }}
            >
                {React.createElement(componentToInsertInModal, {
                    timersPerQuarter,
                    quarterSelected,
                    setEndConfirmationModalOpen,
                    setMatchStatus,
                    matchStatus,
                    matchId,
                    matchData,
                    setMatchData,
                    setEventsToShow,
                    formatApiData,
                    homePlayers,
                    awayPlayers,
                    sides,
                })}
            </ReactModal>
        </>
    );
};

export default LiveTicker;
